import React, { useState } from 'react';

import { Stack } from '@mantine/core';
import { IEventTypeMultiSelectAutoComplete } from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SelfBookingAppointmentCoachAvailableTimeSlot } from 'types/generated';

import AppointmentsView from './components/AppointmentsView';
import { SelfBookingAppointmentCreateSteps } from './components/AppointmentsView/components/types';
import ClassesView from './components/ClassesView';
import ScheduleHeader from './components/ScheduleHeader';

interface FilterTerms {
  eventTypes: IEventTypeMultiSelectAutoComplete[];
  coaches: string[];
  locations: string[];
}

interface BookingStepState {
  selectedAppointmentTypeId: string | null;
  selectedStaffId: string | null;
  selectedDate: Date | null;
  selectedTimeSlot: SelfBookingAppointmentCoachAvailableTimeSlot | null;
  currentMonth: Date;
}

interface SelfBookingAppointmentTypeStepsState {
  step: SelfBookingAppointmentCreateSteps;
  stepState: BookingStepState;
}

const Schedule = (): JSX.Element => {
  const { state } = useLocation();
  const [searchFilters, setSearchFilters] = useState<FilterTerms>({
    eventTypes: state?.eventTypes || [],
    coaches: state?.coaches || [],
    locations: state?.locations || [],
  });

  const [currentStep, setCurrentStep] = useState<SelfBookingAppointmentTypeStepsState>({
    step: 'appointmentType',
    stepState: {
      selectedAppointmentTypeId: null,
      selectedStaffId: null,
      selectedDate: null,
      selectedTimeSlot: null,
      currentMonth: new Date(),
    },
  });

  const [searchParams] = useSearchParams();

  const activeTabSearchParam = searchParams.get('activeTab');
  const activeTab = activeTabSearchParam ? activeTabSearchParam : 'class';

  const isInAppointmentBooking =
    activeTab === 'appointment' && !!currentStep.stepState.selectedAppointmentTypeId;
  const showBackButton = isInAppointmentBooking && currentStep.step !== 'appointmentType';

  const handleStepBack = (): void => {
    if (currentStep.step === 'coach') {
      setCurrentStep({
        step: 'appointmentType',
        stepState: {
          selectedAppointmentTypeId: null,
          selectedStaffId: null,
          selectedDate: null,
          selectedTimeSlot: null,
          currentMonth: new Date(),
        },
      });
    } else if (currentStep.step === 'date') {
      setCurrentStep(prev => ({
        step: 'coach',
        stepState: {
          selectedAppointmentTypeId: prev.stepState.selectedAppointmentTypeId,
          selectedStaffId: prev.stepState.selectedStaffId,
          selectedDate: null,
          selectedTimeSlot: null,
          currentMonth: prev.stepState.currentMonth,
        },
      }));
    } else if (currentStep.step === 'timeSlot') {
      setCurrentStep(prev => ({
        step: 'date',
        stepState: {
          selectedAppointmentTypeId: prev.stepState.selectedAppointmentTypeId,
          selectedStaffId: prev.stepState.selectedStaffId,
          selectedDate: prev.stepState.selectedDate,
          selectedTimeSlot: null,
          currentMonth: prev.stepState.currentMonth,
        },
      }));
    }
  };

  const handleAppointmentReset = (): void => {
    setCurrentStep({
      step: 'appointmentType',
      stepState: {
        selectedAppointmentTypeId: null,
        selectedStaffId: null,
        selectedDate: null,
        selectedTimeSlot: null,
        currentMonth: new Date(),
      },
    });
  };

  return (
    <>
      <ScheduleHeader
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        currentStep={currentStep.step}
        onStepBack={handleStepBack}
        showAppointmentBackButton={showBackButton}
      />
      <Stack className='content' id='scrollable-content'>
        {activeTab === 'class' && <ClassesView searchFilters={searchFilters} />}
        {activeTab === 'appointment' && (
          <AppointmentsView
            searchFilters={searchFilters}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            onReset={handleAppointmentReset}
          />
        )}
      </Stack>
    </>
  );
};

export default Schedule;
